import React, { useState, useEffect } from 'react';
import { Card, Button } from '../components/common';
import StorageService from '../services/storage/storageService';
import { loadDemoData, clearDemoData } from '../utils/generateDemoData';

const DashboardPage = () => {
  const [assessments, setAssessments] = useState([]);
  const [stats, setStats] = useState({ total: 0, inProgress: 0, completed: 0 });
  const [filter, setFilter] = useState('all'); // all, in_progress, completed
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    const allAssessments = await StorageService.getAllAssessments();
    const assessmentArray = Object.values(allAssessments);
    setAssessments(assessmentArray);
    
    const statsData = await StorageService.getStats();
    setStats(statsData);
  };
  
  const filteredAssessments = assessments.filter(a => {
    if (filter === 'all') return true;
    return a.status === filter;
  });
  
  const handleExport = async () => {
    await StorageService.exportData();
  };
  
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this assessment?')) {
      await StorageService.deleteAssessment(id);
      loadData();
    }
  };
  
  const handleLoadDemo = () => {
    loadDemoData();
    loadData();
    alert('✅ Demo student data loaded successfully!\n\nStudent: Aarav Sharma\nGrade: 10-A\nSchool: Delhi Public School');
  };
  
  const handleClearDemo = () => {
    if (window.confirm('Clear demo data?')) {
      clearDemoData();
      loadData();
    }
  };
  
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800">
      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-primary-600 to-primary-700 border-primary-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-primary-100 text-sm font-medium">Total Assessments</p>
                <p className="text-4xl font-bold text-white mt-2">{stats.total}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
          </Card>
          
          <Card className="bg-gradient-to-br from-yellow-600 to-yellow-700 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-100 text-sm font-medium">In Progress</p>
                <p className="text-4xl font-bold text-white mt-2">{stats.inProgress}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-600 to-green-700 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Completed</p>
                <p className="text-4xl font-bold text-white mt-2">{stats.completed}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </Card>
        </div>
        
        {/* Main Table Card */}
        <Card title="All Assessments" subtitle="View and manage student assessments">
          {/* Filters and Actions */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div className="flex gap-2">
              <Button
                variant={filter === 'all' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setFilter('all')}
              >
                All ({stats.total})
              </Button>
              <Button
                variant={filter === 'in_progress' ? 'warning' : 'outline'}
                size="sm"
                onClick={() => setFilter('in_progress')}
              >
                In Progress ({stats.inProgress})
              </Button>
              <Button
                variant={filter === 'completed' ? 'success' : 'outline'}
                size="sm"
                onClick={() => setFilter('completed')}
              >
                Completed ({stats.completed})
              </Button>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="success"
                size="sm"
                onClick={handleLoadDemo}
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                }
              >
                Load Demo Data
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleExport}
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                }
              >
                Export Data
              </Button>
            </div>
          </div>
          
          {/* Table */}
          {filteredAssessments.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto text-slate-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-slate-400 text-lg">No assessments found</p>
              <p className="text-slate-500 text-sm mt-2">Start by creating a new assessment</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Student</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">School</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Grade</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Created</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAssessments.map((assessment) => (
                    <tr key={assessment.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white font-semibold text-sm mr-3">
                            {(assessment.personal_info || assessment.personalInfo)?.firstName?.[0]}{(assessment.personal_info || assessment.personalInfo)?.lastName?.[0]}
                          </div>
                          <div>
                            <p className="text-slate-100 font-medium">
                              {(assessment.personal_info || assessment.personalInfo)?.firstName} {(assessment.personal_info || assessment.personalInfo)?.lastName}
                            </p>
                            <p className="text-slate-500 text-xs">{(assessment.personal_info || assessment.personalInfo)?.email || 'No email'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-300">{(assessment.personal_info || assessment.personalInfo)?.schoolName}</td>
                      <td className="py-3 px-4 text-slate-300">
                        Grade {(assessment.personal_info || assessment.personalInfo)?.grade} - {(assessment.personal_info || assessment.personalInfo)?.section}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          assessment.status === 'completed' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {assessment.status === 'completed' ? 'Completed' : 'In Progress'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-400 text-sm">
                        {formatDate(assessment.created_at || assessment.createdAt)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2 items-center">
                          {assessment.status === 'completed' ? (
                            <>
                              <button
                                onClick={() => window.location.href = `/report/${assessment.id}/nutrition`}
                                className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded transition-colors"
                                title="Nutrition Report"
                              >
                                🥗 Nutrition
                              </button>
                              <button
                                onClick={() => window.location.href = `/report/${assessment.id}/cognitive`}
                                className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded transition-colors"
                                title="Cognitive Report"
                              >
                                🧠 Cognitive
                              </button>
                              <button
                                onClick={() => window.location.href = `/report/${assessment.id}/physical`}
                                className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-colors"
                                title="Physical Report"
                              >
                                💪 Physical
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => window.location.href = `/assessment?id=${assessment.id}`}
                              className="text-yellow-400 hover:text-yellow-300 transition-colors"
                              title="Continue Assessment"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(assessment.id)}
                            className="text-red-400 hover:text-red-300 transition-colors ml-2"
                            title="Delete"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
